/**
 * SDA-287 — faculty headshot download helpers for the Marketing Materials card.
 *
 * Headshots live in public Supabase Storage buckets (faculty-headshots for current
 * uploads, headshots for legacy imports) under their raw upload names, e.g.
 * 1784499057427-IMG_9158.jpeg. Appending `?download=<filename>` makes Storage answer
 * with `content-disposition: attachment; filename=...`, which is what actually forces
 * a save with a usable name — the HTML `download` attribute is ignored by browsers on
 * cross-origin URLs.
 */

import type { AdminClass } from '../api/admin';

export interface FacultyHeadshotRow {
  facultyId: string;
  name: string;
  headshotUrl: string | null; // null => faculty has not uploaded one
  downloadHref: string | null; // null when headshotUrl is null
  filename: string | null; // null when headshotUrl is null
}

/** Image types we are willing to echo back into a download filename. */
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif'];
const DEFAULT_EXTENSION = 'jpg';

/**
 * Extension of the stored object, from the URL path only. Anything unrecognised —
 * no dot, a junk extension, or a URL we cannot parse — falls back to jpg rather than
 * throwing into render.
 */
function extensionFromUrl(url: string): string {
  let pathname: string;
  try {
    pathname = new URL(url).pathname;
  } catch {
    return DEFAULT_EXTENSION;
  }

  const lastDot = pathname.lastIndexOf('.');
  if (lastDot === -1) return DEFAULT_EXTENSION;

  const ext = pathname.slice(lastDot + 1).toLowerCase();
  return ALLOWED_EXTENSIONS.includes(ext) ? ext : DEFAULT_EXTENSION;
}

/**
 * `Alli Echelmeyer` + `.../1784499057427-IMG_9158.jpeg` -> `alli-echelmeyer-headshot.jpeg`.
 * Slug rule matches the show-slug precedent in ShowFormPage.
 */
export function buildHeadshotFilename(name: string, url: string): string {
  const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${slug || 'faculty'}-headshot.${extensionFromUrl(url)}`;
}

/**
 * Public headshot URL plus the `?download=` param. Uses URLSearchParams so an existing
 * query string survives and the filename is encoded.
 */
export function buildHeadshotHref(url: string, filename: string): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('download', filename);
    return parsed.toString();
  } catch {
    // Malformed stored URL (no prod row is one today: all 115 are absolute http).
    // Hand it back untouched rather than inventing a href — the link opens in a new
    // tab, so a dead value costs the admin a tab, never the booking page.
    return url;
  }
}

/**
 * One row per unique faculty on the booking, in class order, keeping the first
 * occurrence — 3 classes taught by 2 people yields 2 rows. Faculty without a headshot
 * still get a row so admins can see who to chase.
 */
export function buildFacultyHeadshotRows(classes: AdminClass[] | undefined): FacultyHeadshotRow[] {
  if (!classes || classes.length === 0) return [];

  const rows: FacultyHeadshotRow[] = [];
  const seen = new Set<string>();

  for (const cls of classes) {
    const faculty = cls.faculty;
    if (!faculty || !faculty.id || seen.has(faculty.id)) continue;
    seen.add(faculty.id);

    // Same fallback chain as the Assigned Faculty row, so the two never disagree.
    const name =
      [faculty.first_name, faculty.last_name].filter(Boolean).join(' ') ||
      faculty.email ||
      'Unknown Faculty';

    const headshotUrl = faculty.headshot_url || null;
    if (!headshotUrl) {
      rows.push({ facultyId: faculty.id, name, headshotUrl: null, downloadHref: null, filename: null });
      continue;
    }

    const filename = buildHeadshotFilename(name, headshotUrl);
    rows.push({
      facultyId: faculty.id,
      name,
      headshotUrl,
      downloadHref: buildHeadshotHref(headshotUrl, filename),
      filename,
    });
  }

  return rows;
}
